import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { CheckCircle2, PauseCircle, RefreshCw, XCircle, Eye, Pencil } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { OverflowMenuContent, RowOverflowMenuTrigger } from '@/components/molecules';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import {
  useActivateMembership,
  useSuspendMembership,
  useReactivateMembership,
} from '@/hooks/memberships/useMembershipActions';
import { CancelSaveFlowSheet } from '@/components/organisms/retention/CancelSaveFlowSheet';
import {
  MEMBERSHIP_DETAIL_SEARCH,
  MEMBERSHIP_LIST_SEARCH,
} from '@/lib/memberships/membership-route-search';
import { dateKeyInTimeZone } from '@/lib/date-values';
import type { Membership } from '@/types/membership';
import { canManuallyActivateMembership } from './support/membership-next-step';

export interface MembershipActionsProps {
  membership: Membership;
  variant: 'row' | 'header';
}

export function MembershipActions({ membership, variant }: MembershipActionsProps) {
  const router = useRouter();
  const companyTimeZone = useCompanyTimezone();
  const id = membership.id;
  const status = membership.status;

  const [cancelOpen, setCancelOpen] = useState(false);

  const activateMutation = useActivateMembership();
  const suspendMutation = useSuspendMembership();
  const reactivateMutation = useReactivateMembership();

  const canActivate = canManuallyActivateMembership({
    ...membership,
    todayDateKey: dateKeyInTimeZone(new Date().toISOString(), companyTimeZone),
  });
  const canSuspend = status === 'ACTIVE';
  const canReactivate = status === 'SUSPENDED' || status === 'CANCELLED';
  const canCancel = status !== 'CANCELLED' && status !== 'EXPIRED';

  const handleView = () =>
    void router.navigate({
      to: '/dashboard/memberships/$id',
      params: { id },
      search: MEMBERSHIP_DETAIL_SEARCH,
    });
  const handleEdit = () =>
    void router.navigate({
      to: '/dashboard/memberships/$id/edit',
      params: { id },
      search: MEMBERSHIP_LIST_SEARCH,
    });

  if (variant === 'header') {
    return (
      <>
        <div className="hidden items-center gap-1.5 md:flex">
          {canActivate && (
            <Button
              size="icon-sm"
              variant="outline"
              title="Activate"
              aria-label="Activate membership"
              onClick={() => activateMutation.mutate(id)}
              disabled={activateMutation.isPending}
            >
              <CheckCircle2 size={14} />
            </Button>
          )}
          {canSuspend && (
            <Button
              size="icon-sm"
              variant="outline"
              title="Suspend"
              aria-label="Suspend membership"
              onClick={() => suspendMutation.mutate(id)}
              disabled={suspendMutation.isPending}
            >
              <PauseCircle size={14} />
            </Button>
          )}
          {canReactivate && (
            <Button
              size="icon-sm"
              variant="outline"
              title="Reactivate"
              aria-label="Reactivate membership"
              onClick={() => reactivateMutation.mutate(id)}
              disabled={reactivateMutation.isPending}
            >
              <RefreshCw size={14} />
            </Button>
          )}
          {canCancel && (
            <Button
              size="icon-sm"
              variant="destructive"
              title="Cancel"
              aria-label="Cancel membership"
              onClick={() => setCancelOpen(true)}
            >
              <XCircle size={14} />
            </Button>
          )}
        </div>
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <RowOverflowMenuTrigger />
            </DropdownMenuTrigger>
            <OverflowMenuContent>
              <DropdownMenuItem onSelect={handleEdit}>
                <Pencil className="mr-2 size-3.5" />
                Edit
              </DropdownMenuItem>
              {canActivate && (
                <DropdownMenuItem onSelect={() => activateMutation.mutate(id)}>
                  <CheckCircle2 className="mr-2 size-3.5" />
                  Activate
                </DropdownMenuItem>
              )}
              {canSuspend && (
                <DropdownMenuItem onSelect={() => suspendMutation.mutate(id)}>
                  <PauseCircle className="mr-2 size-3.5" />
                  Suspend
                </DropdownMenuItem>
              )}
              {canReactivate && (
                <DropdownMenuItem onSelect={() => reactivateMutation.mutate(id)}>
                  <RefreshCw className="mr-2 size-3.5" />
                  Reactivate
                </DropdownMenuItem>
              )}
              {canCancel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setCancelOpen(true);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <XCircle className="mr-2 size-3.5" />
                    Cancel
                  </DropdownMenuItem>
                </>
              )}
            </OverflowMenuContent>
          </DropdownMenu>
        </div>
        <CancelSaveFlowSheet
          entity="membership"
          id={membership.id}
          membership={membership}
          open={cancelOpen}
          onOpenChange={setCancelOpen}
        />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <RowOverflowMenuTrigger />
        </DropdownMenuTrigger>
        <OverflowMenuContent>
          <DropdownMenuItem onSelect={handleView}>
            <Eye className="mr-2 size-3.5" />
            View Details
          </DropdownMenuItem>
          {canActivate && (
            <DropdownMenuItem onSelect={() => activateMutation.mutate(id)}>
              <CheckCircle2 className="mr-2 size-3.5" />
              Activate
            </DropdownMenuItem>
          )}
          {canSuspend && (
            <DropdownMenuItem onSelect={() => suspendMutation.mutate(id)}>
              <PauseCircle className="mr-2 size-3.5" />
              Suspend
            </DropdownMenuItem>
          )}
          {canReactivate && (
            <DropdownMenuItem onSelect={() => reactivateMutation.mutate(id)}>
              <RefreshCw className="mr-2 size-3.5" />
              Reactivate
            </DropdownMenuItem>
          )}
          {canCancel && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setCancelOpen(true);
                }}
                className="text-destructive focus:text-destructive"
              >
                <XCircle className="mr-2 size-3.5" />
                Cancel
              </DropdownMenuItem>
            </>
          )}
        </OverflowMenuContent>
      </DropdownMenu>
      <CancelSaveFlowSheet
        entity="membership"
        id={membership.id}
        membership={membership}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
      />
    </>
  );
}
